<template>
    <ams-sticky-container
        data-component="AmsStickyFilter"
        :mobileVisibilityThreshold="500"
        class="screen-m:mb-25 ml-[-16px] w-screen screen-m:ml-0 screen-m:w-full flex flex-col"
        @update:sticking="updateSticking"
    >
        <div class="flex justify-end screen-m:justify-between">
            <span class="hidden text-text-l-desktop screen-m:block font-bold p-16 py-[18px]">
                {{ totalHits }} Ergebnisse
            </span>
            <div class="justify-between block w-full screen-m:flex screen-m:w-fit">
                <transition
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <button
                        v-show="isFilterButtonVisible"
                        type="button"
                        class="duration-300 inline-flex text-text-l p-16 py-[18px] items-center"
                        aria-label="Filter öffnen"
                        aria-haspopup="true"
                        @click="onClickFilter"
                    >
                        <ms-settings class="w-24 h-24 ml-8 mr-8 hover:cursor-pointer" /> Filter
                        <span
                            class="px-[7px] ml-4 rounded-full font-bold bg-color-signatur-auto-100 text-caption h-[22px] min-w-[22px] items-center justify-center inline-flex"
                        >
                            {{ filterCount }}
                        </span>
                    </button>
                </transition>
                <div class="hidden w-1 h-full screen-m:block bg-wildsand" />
                <ams-sort
                    v-model="amsSearchStore.sort"
                    class="p-16 py-[18px] items-center float-right"
                    :options="carSortOptions"
                    :tracking-data="sortTrackingData"
                />
            </div>
        </div>
        <ams-filter-container v-if="hasStickyFilterTags"> <ams-filter-tags /> </ams-filter-container>
    </ams-sticky-container>
</template>

<script lang="ts" setup>
import { MsSettings } from '@sunrise/icons'
import { commonUtils, screenSizeDetection } from '@sunrise/shared'
import { createTealiumTracking, trackLink } from '@sunrise/tracking'
import { computed, ref } from 'vue'
import { SortKeys } from '../../services/amsSearch/amsSearch.interface'
import { useAmsSearchStore } from '../../store/amsSearch/amsSearch'
import { useUiStore } from '../../store/ui'
import AmsSort from '../AmsSort'
import AmsStickyContainer from '../AmsStickyContainer'
import baseTrackingData from './baseTrackingData'
import { getFilterCount } from './utils'
import AmsFilterContainer from './AmsFilterContainer.vue'
import AmsFilterTags from './AmsFilterTags.vue'

defineProps({
    hasStickyFilterTags: Boolean,
})

const { isScreenM } = screenSizeDetection()

const amsSearchStore = useAmsSearchStore()
const uiStore = useUiStore()

const isSticking = ref(false)
const totalHits = computed(() => commonUtils.thousandSeparator(amsSearchStore?.results?.totalHits ?? 0))
const isFilterButtonVisible = computed(() => isSticking.value || isScreenM.value)

const updateSticking = (sticking: boolean) => {
    isSticking.value = sticking
}

const onClickFilter = (e: MouseEvent) => {
    uiStore.isFilterModalOpen = true
    trackFilter(e)
}

const filterCount = computed(() => getFilterCount(amsSearchStore.currentRequest))

const trackFilter = (e: MouseEvent) => {
    // We need to remove the reference to the object by destructuring because it
    // gets mutated otherwise and the next tracking will contain unwanted data
    const tealiumTrackingData = createTealiumTracking({ ...filterTrackingData })
    trackLink(e, e.target as HTMLAnchorElement, [tealiumTrackingData])
}

const sortTrackingData = {
    ...baseTrackingData,
    mslayer_element_data_id: 'ed0000143',
    mslayer_element_text: 'sticky_sortierung',
}

const filterTrackingData = {
    ...baseTrackingData,
    mslayer_element_data_id: 'ed0000142',
    mslayer_element_text: 'sticky_filterung',
}

enum SortValues {
    OurRecommendationADesc = 'Unsere Empfehlung',
    DistanceAscAndCpcDesc = 'Entfernung',
    LatestOffersAndCpcDesc = 'Neueste',
    FocusOnPriceADesc = 'Preis & Empfehlung',
    PriceAsc = 'Preis',
}

const carSortOptions = [
    { value: SortKeys.OurRecommendationADesc, label: SortValues.OurRecommendationADesc },
    { value: SortKeys.DistanceAscAndCpcDesc, label: SortValues.DistanceAscAndCpcDesc },
    { value: SortKeys.LatestOffersAndCpcDesc, label: SortValues.LatestOffersAndCpcDesc },
    { value: SortKeys.FocusOnPriceADesc, label: SortValues.FocusOnPriceADesc },
    { value: SortKeys.PriceAsc, label: SortValues.PriceAsc },
]
</script>
